import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useCategoriesQuery } from '@/features/projects/categories/hooks/use-categories-query';
import { CreateProjectHeader } from '@/features/projects/create-project/components/layouts/create-project-header';
import {
  CreateProjectStoreSetData,
  useCreateProjectStore,
} from '@/features/projects/create-project/store/use-create-project-store';
import { FlowBackButton } from '@/shared/components/flow/flow-actions';
import { FlowFormFields } from '@/shared/components/flow/flow-fields';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

interface CreateProjectSelectBranchFormProps {
  onSuccess: () => void;
  onBack: () => void;
  onChange: (data: CreateProjectStoreSetData) => void;
}

export const CreateProjectSelectBranchForm = ({
  onSuccess,
  onBack,
  onChange,
}: CreateProjectSelectBranchFormProps) => {
  const { t: projectT } = useTranslation('project', {
    keyPrefix: 'Shared',
  });
  const { t } = useTranslation('project', {
    keyPrefix: 'SelectBranchForm',
  });
  const { t: categoryT } = useTranslation('categories', {
    keyPrefix: 'Level1',
  });

  const { data: categoriesQueryData } = useCategoriesQuery();
  const categories = categoriesQueryData?.categories;

  const {
    data: { selectedCategory },
  } = useCreateProjectStore();

  const formSchema = z.object({
    category: z.string({ message: t('form_field.category.required') }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: selectedCategory,
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    onChange({
      selectedCategory: values.category,
    });
    onSuccess();
  }

  return (
    <>
      <CreateProjectHeader />
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <FlowFormFields>
                <FormField
                  name="category"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger isInputTrigger>
                            <SelectValue
                              asLabelInput
                              label={t('category_label')}
                              placeholder={t('category_placeholder')}
                            >
                              {field.value
                                ? categoryT(field.value as any)
                                : null}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories?.level1.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {categoryT(category.id as any)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FlowFormFields>
            </CardContent>
            <CardFooter>
              <FlowBackButton onClick={onBack}>
                {projectT('back_button_label')}
              </FlowBackButton>
              <Button
                className="w-40"
                variant={'greenGradient'}
                btnType="primary"
                type="submit"
              >
                {t('submit_button_label')}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
